import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

export type ToastVariant = "error" | "success" | "warning" | "info";

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ToastCtx {
  toasts: Toast[];
  show: (message: string, variant?: ToastVariant, durationMs?: number) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastCtx | null>(null);

const DEFAULT_DURATION_MS: Record<ToastVariant, number> = {
  error: 6000,
  warning: 6000,
  success: 4000,
  info: 4000,
};

const VARIANT_COLOR: Record<ToastVariant, string> = {
  error: "var(--color-error)",
  warning: "var(--color-warning)",
  success: "var(--color-success)",
  info: "var(--color-info)",
};

const VARIANT_ROLE: Record<ToastVariant, { role: string; ariaLive: "assertive" | "polite" }> = {
  error: { role: "alert", ariaLive: "assertive" },
  warning: { role: "alert", ariaLive: "assertive" },
  success: { role: "status", ariaLive: "polite" },
  info: { role: "status", ariaLive: "polite" },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timeouts = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    const t = timeouts.current.get(id);
    if (t) { clearTimeout(t); timeouts.current.delete(id); }
    setToasts(prev => prev.filter(x => x.id !== id));
  }, []);

  const show = useCallback((message: string, variant: ToastVariant = "info", durationMs?: number) => {
    const duration = durationMs ?? DEFAULT_DURATION_MS[variant];

    setToasts(prev => {
      const dup = prev.find(t => t.message === message && t.variant === variant);
      if (dup) {
        const existing = timeouts.current.get(dup.id);
        if (existing) clearTimeout(existing);
        timeouts.current.set(dup.id, setTimeout(() => dismiss(dup.id), duration));
        return prev;
      }

      const id = crypto.randomUUID();
      timeouts.current.set(id, setTimeout(() => dismiss(id), duration));
      return [...prev, { id, message, variant }];
    });
  }, [dismiss]);

  useEffect(() => {
    const map = timeouts.current;
    return () => { map.forEach(t => clearTimeout(t)); map.clear(); };
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, show, dismiss }}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastCtx {
  const ctx = useContext(ToastContext);
  return ctx ?? { toasts: [], show: () => {}, dismiss: () => {} };
}

function ToastViewport({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      role="region"
      aria-label="Notifications"
      style={{
        position: "fixed", top: "var(--space-4)", right: "var(--space-4)", zIndex: 10000,
        display: "flex", flexDirection: "column", gap: "var(--space-2)", pointerEvents: "none",
      }}
    >
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateX(16px); }
          to { opacity: 1; transform: none; }
        }
      `}</style>
      {toasts.map(t => {
        const { role, ariaLive } = VARIANT_ROLE[t.variant];
        return (
          <div
            key={t.id}
            role={role}
            aria-live={ariaLive}
            aria-atomic="true"
            style={{
              pointerEvents: "auto",
              display: "flex", alignItems: "flex-start", gap: "var(--space-2)",
              minWidth: "280px", maxWidth: "420px",
              background: "var(--color-surface-0)",
              borderLeft: `4px solid ${VARIANT_COLOR[t.variant]}`,
              borderRadius: "var(--radius-md)",
              boxShadow: "var(--shadow-lg)",
              padding: "var(--space-3) var(--space-4)",
              fontFamily: "var(--font-display)",
              fontSize: "13px",
              color: "var(--color-surface-900)",
              animation: "toast-in 180ms ease-out",
            }}
          >
            <div style={{ flex: 1 }}>{t.message}</div>
            <button
              aria-label="Dismiss notification"
              onClick={() => onDismiss(t.id)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-surface-700)", fontSize: "14px", lineHeight: 1 }}
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}
